// Store reactiv pentru FILTRELE GLOBALE ale dashboard-ului.
//
// Separat de `store.ts` (sursa de date) intenționat: schimbarea unui filtru NU
// remontează dashboard-ul (fără scroll-jump) — doar re-randează secțiunile care
// citesc `useFilters()`. Filtrele conduc getterele derivate din `derived.ts`.
//
// Model:
//   - perioadă: preset (ultimele 1/3/6/12 luni) SAU interval custom [from,to]
//   - client / categorie / segment: contextuale (se aplică pe rapoartele cu grain comandă)
//   - masina: contextual (secțiunea Încărcare mașini)
//
// Fereastra efectivă [from,to] se rezolvă la CITIRE față de lista reală de luni
// din date (vezi `derived.ts`), ca presetul „ultimele 6 luni" să funcționeze
// și pe demo, și pe date importate cu alt orizont.

import { useSyncExternalStore } from "react";

export type PerioadaPreset = "1" | "3" | "6" | "12" | "all" | "custom";

export interface FilterState {
  preset: PerioadaPreset;
  /** luna_key AAAA-LL — folosite doar când preset === "custom". */
  customFrom: string | null;
  customTo: string | null;
  client: string; // "all" sau nume client
  categorie: string; // "all" sau cheie categorie
  segment: "all" | "export" | "intern";
  masina: string; // "all" sau nume mașină
}

export const FILTER_DEFAULT: FilterState = {
  preset: "all",
  customFrom: null,
  customTo: null,
  client: "all",
  categorie: "all",
  segment: "all",
  masina: "all",
};

let state: FilterState = { ...FILTER_DEFAULT };
let version = 0;
const listeners = new Set<() => void>();

function emit() {
  version += 1;
  listeners.forEach((l) => l());
}

export function subscribeFilters(cb: () => void): () => void {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

export function getFilters(): FilterState {
  return state;
}

function getVersion() {
  return version;
}

/** Hook: întoarce starea curentă a filtrelor + re-randează la orice schimbare. */
export function useFilters(): FilterState {
  return useSyncExternalStore(subscribeFilters, getFilters, getFilters);
}

/** Hook ușor: doar versiunea (pentru componente care recitesc singure filtrele). */
export function useFilterVersion(): number {
  return useSyncExternalStore(subscribeFilters, getVersion, getVersion);
}

export function setFilter<K extends keyof FilterState>(key: K, value: FilterState[K]) {
  if (state[key] === value) return;
  state = { ...state, [key]: value };
  emit();
}

export function setPreset(preset: PerioadaPreset) {
  state = { ...state, preset };
  emit();
}

export function setCustomRange(from: string | null, to: string | null) {
  state = { ...state, preset: "custom", customFrom: from, customTo: to };
  emit();
}

export function resetFilters() {
  state = { ...FILTER_DEFAULT };
  emit();
}

/** Câte filtre non-default sunt active (pentru badge „N filtre active"). */
export function activeFilterCount(f: FilterState = state): number {
  let n = 0;
  if (f.preset !== "all") n += 1;
  if (f.client !== "all") n += 1;
  if (f.categorie !== "all") n += 1;
  if (f.segment !== "all") n += 1;
  if (f.masina !== "all") n += 1;
  return n;
}
